<script>
  import { onMount } from "svelte";
  import Icon from "@iconify/svelte";

  import settingsIcon from "@iconify/icons-mdi/cog-outline";
  import refreshIcon from "@iconify/icons-mdi/refresh";
  import logoutIcon from "@iconify/icons-mdi/logout";
  import pauseIcon from "@iconify/icons-mdi/pause";
  import playIcon from "@iconify/icons-mdi/play";
  import monitorIcon from "@iconify/icons-mdi/monitor-dashboard";
  import menuIcon from "@iconify/icons-mdi/menu";
  import closeIcon from "@iconify/icons-mdi/close";
  import { locales, t } from "./lib/i18n.js";
  import LanguageSwitcher from "./components/LanguageSwitcher.svelte";

  import FileBrowser from "./FileBrowser.svelte";
  import Settings from "./Settings.svelte";
  import Runtime from "./Runtime.svelte";
  import Login from "./Login.svelte";

  const DEV_PORTS = new Set(["5173", "4173"]);
  const isViteDev = location.hostname === "localhost" && DEV_PORTS.has(location.port);
  const API_BASE = isViteDev ? "http://localhost:8080" : "";

  const JSON_HEADERS = { Accept: "application/json" };
  const STORAGE_KEY = "depot_api_key";
  const LOCALE_STORAGE = "depot_locale";

  const AUTH_MODE_BOOT = (() => {
    if (typeof document === "undefined") return false;
    const s = document.querySelector('script[type="module"][data-authenticate]');
    const raw = (s?.dataset?.authenticate || "").toString().trim().toLowerCase();
    return raw === "true" || raw === "1" || raw === "yes" || raw === "on";
  })();

  let authMode = AUTH_MODE_BOOT;
  let screen = "files";
  let locale = "en";

  let connected = false;
  let paused = false;
  let dashRefresh = null;
  let connectionTimer = null;
  let connectionInflight = null;

  let setup = null;
  let setupErr = "";
  let buckets = [];
  let selectedBucket = "";
  let permissions = { download: true, rename: true, delete: true, upload: false };
  let mobileMenuOpen = false;

  let apiKey = "";
  let authed = !authMode;
  let booted = false;
  let authErr = "";

  function readKey() {
    try {
      return (localStorage.getItem(STORAGE_KEY) || "").trim();
    } catch {
      return "";
    }
  }

  function readLocale() {
    try {
      const value = (localStorage.getItem(LOCALE_STORAGE) || "en").toLowerCase();
      return locales.includes(value) ? value : "en";
    } catch {
      return "en";
    }
  }

  function setKey(next) {
    const v = (next || "").trim();
    try {
      if (!v) localStorage.removeItem(STORAGE_KEY);
      else localStorage.setItem(STORAGE_KEY, v);
      window.dispatchEvent(new Event("depot-auth-changed"));
    } catch {}
  }

  function syncAuthFromStorage() {
    apiKey = readKey();
    authed = !authMode || apiKey !== "";

    if (!authed) {
      connected = false;
    }
  }

  function authHeaders() {
    return apiKey ? { Authorization: `Bearer ${apiKey}` } : {};
  }

  async function pollConnection() {
    if (authMode && !apiKey) {
      connected = false;
      return;
    }

    try {
      connectionInflight?.abort?.();
    } catch {}
    connectionInflight = new AbortController();

    try {
      const res = await fetch(`${API_BASE}/setup`, {
        method: "GET",
        cache: "no-store",
        signal: connectionInflight.signal,
        headers: { ...JSON_HEADERS, ...authHeaders() }
      });

      if (res.status === 401) {
        onUnauthorized(apiKey ? "Invalid API key" : "Authentication required");
        return;
      }

      connected = res.ok;
    } catch (e) {
      if (e?.name === "AbortError") return;
      connected = false;
    }
  }

  function startConnectionPolling() {
    if (connectionTimer) return;
    pollConnection();
    connectionTimer = setInterval(pollConnection, 2500);
  }

  function stopConnectionPolling() {
    if (!connectionTimer) return;
    clearInterval(connectionTimer);
    connectionTimer = null;
    try {
      connectionInflight?.abort?.();
    } catch {}
  }

  function onUnauthorized(msg = "Authentication required") {
    authErr = msg;
    authMode = true;
    authed = false;
    connected = false;
    setup = null;
    setupErr = "";
    screen = "files";
    setKey("");
    apiKey = "";
  }

  function handleLocaleChange(code) {
    locale = locales.includes(code) ? code : "en";
    try {
      localStorage.setItem(LOCALE_STORAGE, locale);
    } catch {}
  }

  function go(to) {
    screen = to;
  }

  function goFilesRoot(clearQuery = false) {
    screen = "files";
    window.dispatchEvent(new CustomEvent("depot-nav-root", { detail: { clearQuery } }));
  }

  function goBucketRoot() {
    screen = "files";
    window.dispatchEvent(new Event("depot-nav-bucket"));
  }

  function doRefresh() {
    if (typeof dashRefresh === "function") dashRefresh();
  }

  function togglePause() {
    paused = !paused;
  }

  async function prefetchSetup() {
    setupErr = "";
    try {
      const res = await fetch(`${API_BASE}/setup`, {
        method: "GET",
        cache: "no-store",
        headers: { ...JSON_HEADERS, ...authHeaders() }
      });

      if (res.status === 401) {
        onUnauthorized(apiKey ? "Invalid API key" : "Authentication required");
        return;
      }

      if (!res.ok) throw new Error(`setup failed: ${res.status}`);

      setup = await res.json();
      buckets = setup.buckets || [];
      if (buckets.length > 0 && !selectedBucket) {
        selectedBucket = buckets[0].name;
      }
      permissions = {
        download: setup.allow_download !== false,
        rename: setup.allow_rename !== false,
        delete: setup.allow_delete !== false,
        upload: setup.allow_upload === true
      };
      connected = true;
      if (authMode) authed = true;
    } catch (e) {
      setup = null;
      setupErr = e?.message || String(e);
      connected = false;
    }
  }

  onMount(() => {
    locale = readLocale();
    syncAuthFromStorage();
    booted = true;

    const onStorage = (e) => {
      if (e?.key === STORAGE_KEY) syncAuthFromStorage();
      if (e?.key === LOCALE_STORAGE) locale = readLocale();
    };
    const onCustom = () => syncAuthFromStorage();

    window.addEventListener("storage", onStorage);
    window.addEventListener("depot-auth-changed", onCustom);

    prefetchSetup();
    startConnectionPolling();

    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("depot-auth-changed", onCustom);
      stopConnectionPolling();
    };
  });

  $: if (authMode && !authed) {
    screen = "files";
    setup = null;
    setupErr = "";
  }

  let didPrefetchAfterLogin = false;
  $: if (authed && !didPrefetchAfterLogin) {
    didPrefetchAfterLogin = true;
    prefetchSetup();
  }
  $: if (!authed) {
    didPrefetchAfterLogin = false;
    if (authMode) stopConnectionPolling();
  }
  $: if (authed && !connectionTimer) {
    startConnectionPolling();
  }

  const iconBtnBase =
    "inline-flex items-center justify-center w-10 h-10 rounded-2xl border " +
    "transition-all select-none " +
    "border-white/10 bg-white/[0.035] hover:bg-white/[0.06] " +
    "text-zinc-100/90 hover:text-white";

  const dividerCls = "w-px h-6 bg-white/10 mx-2";
</script>

<div class="min-h-screen bg-black text-zinc-100">
  <div class="pointer-events-none fixed inset-0 -z-10">
    <div class="absolute inset-0 bg-[radial-gradient(1200px_560px_at_15%_-10%,rgba(56,189,248,0.14),transparent_55%)]"></div>
    <div class="absolute inset-0 bg-[radial-gradient(900px_520px_at_85%_10%,rgba(255,255,255,0.08),transparent_62%)]"></div>
    <div class="absolute inset-0 bg-[radial-gradient(700px_520px_at_75%_90%,rgba(56,189,248,0.05),transparent_60%)]"></div>
    <div class="absolute inset-0 bg-gradient-to-b from-[#07070a] via-[#050508] to-black"></div>
  </div>

  {#if !booted}
    <div class="min-h-screen grid place-items-center text-zinc-400 text-sm">
      Loading…
    </div>
  {:else if authMode && !authed}
    <Login
      apiBase={API_BASE}
      jsonHeaders={JSON_HEADERS}
      storageKey={STORAGE_KEY}
      error={authErr}
      {locale}
      onLocaleChange={handleLocaleChange}
      onSave={(k) => {
        authErr = "";
        setKey(k);
      }}
    />
  {:else}
    <div class="pt-4 px-4 sm:px-6">
      <header class="sticky top-3 z-50 ui-topbar">
        <div class="relative w-full px-4 py-3">
          <div class="flex items-center justify-between gap-3">
            <div class="flex items-center gap-3 min-w-0">
              <button
                type="button"
                class="flex items-center gap-3 text-left min-w-0"
                onclick={() => goFilesRoot(true)}
                aria-label={t(locale, "go_to_file_browser")}
              >
                <div class="ui-logo text-semibold flex-shrink-0" aria-hidden="true">
                  <Icon icon="glyphs:truck-bold" width="18" height="18" />
                </div>
                <div class="flex flex-col justify-center leading-tight min-w-0">
                  <div class="text-lg font-semibold tracking-wide text-white truncate">{t(locale, "app_name")}</div>
                  <div class="text-xs text-zinc-400 truncate hidden sm:block">
                    {setup?.engine || "S3/MinIO"} {t(locale, "app_subtitle").toLowerCase()}
                  </div>
                </div>
              </button>

            </div>

            <div class="flex items-center justify-end gap-3">
              <button
                type="button"
                class="inline-flex items-center justify-center h-10 w-10 flex-shrink-0 rounded-2xl border border-white/10 bg-white/[0.035] hover:bg-white/[0.06] md:hidden"
                title={t(locale, "menu")}
                aria-controls="app-mobile-menu"
                aria-expanded={mobileMenuOpen}
                onclick={() => mobileMenuOpen = !mobileMenuOpen}
              >
                <Icon icon={mobileMenuOpen ? closeIcon : menuIcon} width="18" height="18" />
              </button>

              <div class="hidden md:flex flex-nowrap items-center justify-end gap-3">
                <div class="flex h-10 items-center gap-2">
                  <div
                    class="inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/10 bg-white/[0.03]"
                    title={connected ? t(locale, "connected") : t(locale, "disconnected")}
                    aria-label={connected ? t(locale, "connected") : t(locale, "disconnected")}
                  >
                    <span
                      class={`h-3 w-3 rounded-full ${
                        connected ? "bg-emerald-400" : "bg-rose-400"
                      }`}
                    ></span>
                  </div>

                  <button
                    type="button"
                    class={`${iconBtnBase} ${paused ? "opacity-80" : ""}`}
                    title={paused ? t(locale, "resume_polling") : t(locale, "pause_polling")}
                    onclick={togglePause}
                  >
                    <Icon icon={paused ? playIcon : pauseIcon} width="18" height="18" />
                  </button>

                  <button
                    type="button"
                    class={iconBtnBase}
                    title={t(locale, "refresh")}
                    onclick={doRefresh}
                  >
                    <Icon icon={refreshIcon} width="18" height="18" />
                  </button>

                  <div class={dividerCls}></div>
                </div>

                {#if buckets.length > 0}
                  <div class="mr-2 hidden md:block">
                    <select
                      bind:value={selectedBucket}
                      class="h-10 min-w-[11rem] px-3 bg-black border border-white/10 rounded-xl text-white text-sm focus:outline-none focus:ring-2 focus:ring-sky-400/20"
                    >
                      {#each buckets as bucket}
                        <option value={bucket.name}>{bucket.name}</option>
                      {/each}
                    </select>
                  </div>
                {/if}

                <nav class="topnav topnav--nowrap h-10">

                  <button class="topnav__tab" class:is-active={screen === "settings"} onclick={() => go("settings")}>
                    <Icon icon={settingsIcon} width="16" height="16" />
                    <span>{t(locale, "nav_settings")}</span>
                  </button>

                  <button class="topnav__tab" class:is-active={screen === "runtime"} onclick={() => go("runtime")}>
                    <Icon icon={monitorIcon} width="16" height="16" />
                    <span>{t(locale, "nav_runtime")}</span>
                  </button>

                </nav>
                <LanguageSwitcher bind:locale onchange={handleLocaleChange} />

                {#if authMode}
                  <div class="flex h-10 items-center gap-2">
                    <div class="w-px h-6 bg-white/10 mx-2"></div>
                    <button
                      type="button"
                      class="inline-flex items-center justify-center w-10 h-10 rounded-2xl border border-white/10 bg-white/[0.035] hover:bg-white/[0.06] transition-all"
                      title={t(locale, "logout")}
                      aria-label={t(locale, "logout")}
                      onclick={() => {
                        authErr = "";
                        setKey("");
                      }}
                    >
                      <Icon icon={logoutIcon} width="18" height="18" />
                    </button>
                  </div>
                {/if}
              </div>
            </div>
          </div>

          {#if mobileMenuOpen}
            <div
              id="app-mobile-menu"
              class="absolute top-full left-0 right-0 mt-2 mx-2 p-4 rounded-2xl border border-white/10 bg-[#09090b] shadow-2xl z-[100] flex flex-col gap-4 md:hidden"
            >
              {#if buckets.length > 0}
                <div class="flex flex-col gap-1">
                  <label for="mobile-bucket-select" class="text-xs font-medium text-zinc-500 uppercase tracking-wide">
                    {t(locale, "bucket")}
                  </label>
                  <select
                    id="mobile-bucket-select"
                    bind:value={selectedBucket}
                    class="h-10 px-3 bg-black/30 border border-white/10 rounded-xl text-white text-sm focus:outline-none focus:ring-2 focus:ring-sky-400/20 w-full"
                  >
                    {#each buckets as bucket}
                      <option value={bucket.name}>{bucket.name}</option>
                    {/each}
                  </select>
                </div>
              {/if}

              <div class="flex flex-col gap-1">
                <span class="text-xs font-medium text-zinc-500 uppercase tracking-wide">{t(locale, "status")}</span>
                <div class="flex items-center justify-between gap-2 p-2 rounded-lg bg-white/[0.03] border border-white/10">
                  <div class="flex items-center gap-2">
                    <div
                      class="inline-flex h-9 w-9 items-center justify-center rounded-full border border-white/10 bg-black/40"
                      title={connected ? t(locale, "connected") : t(locale, "disconnected")}
                      aria-label={connected ? t(locale, "connected") : t(locale, "disconnected")}
                    >
                      <span
                        class={`h-3 w-3 rounded-full ${
                          connected ? "bg-emerald-400" : "bg-rose-400"
                        }`}
                      ></span>
                    </div>
                    <span class="text-sm text-zinc-200">
                      {connected ? t(locale, "connected") : t(locale, "disconnected")}
                    </span>
                  </div>
                  <div class="flex items-center gap-2">
                    <button
                      type="button"
                      class="p-2 rounded-lg hover:bg-white/10 transition-colors text-zinc-400 hover:text-white"
                      title={paused ? t(locale, "resume") : t(locale, "pause")}
                      onclick={togglePause}
                    >
                      <Icon icon={paused ? playIcon : pauseIcon} width="18" height="18" />
                    </button>
                    <button
                      type="button"
                      class="p-2 rounded-lg hover:bg-white/10 transition-colors text-zinc-400 hover:text-white"
                      title={t(locale, "refresh")}
                      onclick={doRefresh}
                    >
                      <Icon icon={refreshIcon} width="18" height="18" />
                    </button>
                  </div>
                </div>
              </div>

              <div class="flex flex-col gap-1">
                <span class="text-xs font-medium text-zinc-500 uppercase tracking-wide">{t(locale, "navigation")}</span>
                <nav class="flex flex-col gap-1">
                  <button
                    class="w-full px-4 py-3 text-left rounded-xl hover:bg-white/5 transition-colors flex items-center gap-3"
                    class:bg-white={false}
                    class:bg-opacity-5={screen === "files"}
                    onclick={() => { goBucketRoot(); mobileMenuOpen = false; }}
                  >
                    <Icon icon="entypo:bucket" width="18" height="18" class={screen === "files" ? "text-sky-400" : "text-zinc-400"} />
                    <span class={screen === "files" ? "text-white font-medium" : "text-zinc-300"}>{t(locale, "nav_storage")}</span>
                  </button>

                  <button
                    class="w-full px-4 py-3 text-left rounded-xl hover:bg-white/5 transition-colors flex items-center gap-3"
                    class:bg-white={false}
                    class:bg-opacity-5={screen === "runtime"}
                    onclick={() => { go("runtime"); mobileMenuOpen = false; }}
                  >
                    <Icon icon={monitorIcon} width="18" height="18" class={screen === "runtime" ? "text-sky-400" : "text-zinc-400"} />
                    <span class={screen === "runtime" ? "text-white font-medium" : "text-zinc-300"}>{t(locale, "nav_runtime")}</span>
                  </button>

                  <button
                    class="w-full px-4 py-3 text-left rounded-xl hover:bg-white/5 transition-colors flex items-center gap-3"
                    class:bg-white={false}
                    class:bg-opacity-5={screen === "settings"}
                    onclick={() => { go("settings"); mobileMenuOpen = false; }}
                  >
                    <Icon icon={settingsIcon} width="18" height="18" class={screen === "settings" ? "text-sky-400" : "text-zinc-400"} />
                    <span class={screen === "settings" ? "text-white font-medium" : "text-zinc-300"}>{t(locale, "nav_settings")}</span>
                  </button>
                </nav>
              </div>

              <div class="pt-1">
                <LanguageSwitcher bind:locale onchange={handleLocaleChange} />
              </div>

              {#if authMode}
                <div class="pt-2 border-t border-white/10">
                  <button
                    type="button"
                    class="w-full px-4 py-3 text-left rounded-xl hover:bg-rose-500/10 transition-colors flex items-center gap-3 text-rose-400"
                    onclick={() => {
                      authErr = "";
                      setKey("");
                      mobileMenuOpen = false;
                    }}
                  >
                    <Icon icon={logoutIcon} width="18" height="18" />
                    <span>{t(locale, "logout")}</span>
                  </button>
                </div>
              {/if}
            </div>
          {/if}
        </div>
      </header>
    </div>

    {#if screen === "files"}
      <FileBrowser
        apiBase={API_BASE}
        {apiKey}
        bucket={selectedBucket}
        {permissions}
        bind:paused
        setRefresh={(fn) => (dashRefresh = fn)}
        {onUnauthorized}
      />
    {:else if screen === "settings"}
      <Settings {setup} setupErr={setupErr} />
    {:else if screen === "runtime"}
      <Runtime apiBase={API_BASE} {apiKey} />
    {/if}
  {/if}
</div>
